import React, { useEffect, useState, Suspense } from 'react';
import jwt from 'jsonwebtoken';
import { Grid, Row, Col } from 'antd';
import { Modal, Button } from './components';
import moment from 'moment-timezone';
import { useHistory, useLocation } from 'react-router';
import { Switch as RouterSwitch, Route, matchPath } from 'react-router-dom';
import { t } from 'i18next';

import ScrollToTop from './hooks/ScrollToTop';
import FilterContextProvider from './providers/FilterContextProvider';
import { Layout, Spin, message } from './components';
import { Header, Footer, CloseButton } from './shared';
import Context, { state, initialState } from './context';
import BookmarksProvider from './bookmarksContext';
import { privateRoutes, publicRoutes } from './routes';
import { AxiosInterceptor } from './axios';
import { getLocation } from './utils';
import * as constants from './constants';
import PageLoader from './shared/PageLoader';
import { getUserSubscriptions, getSessionID, globalSignOut } from './services';
import { getProfileImage } from './services/user';
import { lazyWithRetry } from './customReactLazy';
import { Condition } from './components';
import IdleTimeoutManager from './components/IdleTimeoutManager';

import './App.less';
import './util.module.less';
import './i18n';

const Home = lazyWithRetry(() => import('./pages/Home'));
const Page404 = lazyWithRetry(() => import('./pages/404'));
const PageAccessDenied = lazyWithRetry(() => import('./pages/AccessDenied'));

// app defaults
moment.tz.setDefault('Asia/Kolkata');

const { Content } = Layout;

const App = () => {
  const screens = Grid.useBreakpoint();
  const [context, setContext] = useState(state);
  const [routes, setRoutes] = useState(publicRoutes());
  const [mobileDisclaimer, setMobileDisclaimer] = useState(false);
  const [show, setShow] = useState(false);

  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  var userback = window.Userback;

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'logoutEvent') {
        setContext((ps) => ({
          ...initialState,
          screenContext: ps.screenContext,
          isAuth: false,
        }));

        history.push('/');

        localStorage.removeItem('logoutEvent');
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    /**
     * If profile picture is set which will be a filename and avatar is not a valid image url
     * Fetch S3 signed url from the server of the image using the filename
     */
    if (context.profile?.profile && !context.avatar && !context.loadingAvatar) {
      setContext((state) => ({ ...state, loadingAvatar: true }));
      const fetchAvatar = async () => {
        const {
          data: { IsError = false, URL = '' },
        } = await getProfileImage(context.profile.profile);

        if (!IsError) {
          setContext((state) => ({ ...state, avatar: URL, loadingAvatar: false }));
        } else {
          setContext((state) => ({ ...state, loadingAvatar: false }));
        }
      };

      fetchAvatar();
    }
  }, [context.profile, context.avatar, context.loadingAvatar]);

  useEffect(() => {
    if (context.screenContext === 'mobile') setMobileDisclaimer(true);
  }, [context.screenContext]);

  useEffect(() => {
    if (context?.profile?.email && userback) {
      userback.name = context.profile.name;
      userback.email = context.profile.email;
      var widget_settings = {
        categories: [
          'Incorrect Search Results',
          'Data not available',
          'In-appropriate visualization',
          'In-appropriate analysis',
          'Data Error',
          'Dataset',
          'Other',
        ],
        main_button_text: t('feedback'),
        main_button_background_colour: '#3E3F3A',
        main_button_text_colour: '#FFFFFF',
        name_field: context.profile.name,
        email_field: context.profile.email,
      };
      userback.setName?.(context.profile.name);
      userback.setEmail?.(context.profile.email);
      userback.setWidgetSettings?.(widget_settings);
    }
    // eslint-disable-next-line
  }, [userback]);

  const logout = async () => {
    try {
      await globalSignOut();

      // Clear sessions across all tabs and windows
      sessionStorage.clear();

      setContext((ps) => ({
        ...initialState,
        isLoading: false,
        screenContext: ps.screenContext,
        isAuth: false,
        preferences: { ...ps.preferences },
      }));

      // Broadcast logout event to all tabs
      localStorage.setItem('logoutEvent', Date.now().toString());
      localStorage.removeItem('isAnyTabActive');
    } catch (error) {
      message.error(error);
    }
  };

  useEffect(() => {
    const localToken = sessionStorage.getItem('token');
    const redirectPath = `${location.pathname}?${params.toString()}`;

    if (localToken) {
      setContext((ps) => ({
        ...ps,
        isAuth: true,
        profile: localToken ? jwt.decode(localToken) : null,
        afterLoginRedirect: redirectPath,
        showLogin: false,
      }));
    }

    // eslint-disable-next-line
  }, []);

  // useEffect(() => {
  //   // logout if user is inactive for more than 30 minutes
  //   const inactivityTime = () => {
  //     let time;
  //     const resetTimer = () => {
  //       clearTimeout(time);
  //       time = setTimeout(logout, 1000 * 60 * 30); // 30 minutes
  //     };

  //     window.onload = resetTimer;
  //     document.onmousemove = resetTimer;
  //     document.onkeydown = resetTimer;
  //   };

  //   inactivityTime();
  //   // eslint-disable-next-line
  // }, []);

  useEffect(() => {
    localStorage.setItem('page-focus', new Date());
    // logout if browser focus is out for more than 30 minutes
    window.onfocus = async function () {
      if ((new Date().getTime() - new Date(localStorage.getItem('page-focus')).getTime()) / 60000 > 30) {
        if (sessionStorage.getItem('sessionId') == null || sessionStorage.getItem('sessionId') === '') {
          const sessionRes = await getSessionID();
          if (sessionRes?.data?.session_id) {
            sessionStorage.setItem('sessionId', sessionRes.data.session_id);
          } else {
            console.error('Failed to fetch or store session ID.');
          }
        }
      } else {
        localStorage.setItem('page-focus', new Date());
      }
    };

    window.onblur = function () {
      localStorage.setItem('page-focus', new Date());
    };
  }, []);

  useEffect(() => {
    getLocation();
    setContext((ps) => ({ ...ps, logoutFn: logout }));
    // eslint-disable-next-line
  }, [context.isAuth]);

  useEffect(() => {
    if (Object.keys(screens).length > 0) {
      if (screens.xxl || screens.xl) setContext((state) => ({ ...state, screenContext: constants.ScreenSize.Desktop }));
      else if (screens.lg || screens.md)
        setContext((state) => ({ ...state, screenContext: constants.ScreenSize.Tablet }));
      else setContext((state) => ({ ...state, screenContext: constants.ScreenSize.Mobile }));
    }
  }, [screens]);

  useEffect(() => {
    const init = async () => {
      if (context?.profile?.email) {
        setContext((ps) => ({ ...ps, subscriptions: { ...ps.subscriptions, loading: true } }));
        getUserSubscriptions({
          userID: context?.profile?.email,
          setData: (data) => {
            setContext((ps) => ({ ...ps, subscriptions: data }));
          },
        });
      }

      if (context.profile?.picture && !context.avatar) {
        try {
          const {
            data: { URL },
          } = await getProfileImage(context.profile?.picture);

          if (URL) setContext((ps) => ({ ...ps, avatar: URL }));
        } catch (e) {
          console.log(e);
        }
      }
    };

    init();

    if (context.isAuth) setRoutes([...privateRoutes(), ...publicRoutes()]);
    else setRoutes(publicRoutes());
    // eslint-disable-next-line
  }, [context.isAuth]);

  useEffect(() => {
    // use effect for checking if user is not logged in but opening a log in only route
    const path = window.location.pathname;
    const redirectPath = `${location.pathname}?${params.toString()}`;

    if (!context.isAuth && path !== '/') {
      let isValidRoute = false;
      privateRoutes().forEach((route) => {
        if (matchPath(path, route.path)?.isExact) isValidRoute = true;
      });
      if (isValidRoute) {
        setContext((ps) => ({ ...ps, afterLoginRedirect: redirectPath, showLogin: true }));
        setShow(true);
      }
    }

    // eslint-disable-next-line
  }, [context.isAuth, location.pathname]);

  return (
    <Context.Provider value={[context, setContext]}>
      <AxiosInterceptor logout={logout}>
        <FilterContextProvider>
          <BookmarksProvider>
            <Spin size="large" spinning={context.isLoading}>
              <Layout style={{ height: '100vh' }} className={`zoomSize${context.fontSize}`}>
                <Content>
                  <Row>
                    <Col span={24}>
                      <Header />
                      <IdleTimeoutManager onLogout={logout} />
                    </Col>
                    <Col
                      span={24}
                      style={{
                        minHeight:
                          context.screenContext === constants.ScreenSize.Desktop
                            ? 'calc(100vh - 285px)'
                            : 'calc(100vh - 445px)',
                      }}
                    >
                      <Suspense fallback={<PageLoader />}>
                        <ScrollToTop>
                          <RouterSwitch>
                            <Route exact path="/">
                              <Home />
                            </Route>
                            {routes.map((r) => (
                              <Route key={r.path} path={r.path} exact={true} component={r.component} />
                            ))}
                            <Route path="*">
                              <Condition show={!show}>
                                <Page404 />
                              </Condition>
                              <Condition show={show}>
                                <PageAccessDenied />
                              </Condition>
                            </Route>
                          </RouterSwitch>
                        </ScrollToTop>
                      </Suspense>
                    </Col>
                    <Col span={24}>
                      <Footer />
                    </Col>
                    <Modal
                      title={[
                        <div className="pr-4" key={'mobile-notice'}>
                          {t('MobileNotice')}
                        </div>,
                      ]}
                      open={mobileDisclaimer}
                      onCancel={() => setMobileDisclaimer(false)}
                      width={800}
                      footer={[
                        <Button key="submit-proceed" type="primary" onClick={() => setMobileDisclaimer(false)}>
                          {t('Proceed')}
                        </Button>,
                      ]}
                      closeIcon={<CloseButton />}
                    >
                      <p>{t('MobileNoticeText')}</p>
                    </Modal>
                  </Row>
                </Content>
              </Layout>
            </Spin>
          </BookmarksProvider>
        </FilterContextProvider>
      </AxiosInterceptor>
    </Context.Provider>
  );
};

export default App;
